/**
 * Infrastructure Element Colors Store — user-customisable colors per element type
 *
 * @module core/modules/infrastructure/store/element-colors-store
 *
 * @description
 * Zustand store with localStorage persistence. Keeps only user overrides;
 * falls back to INFRASTRUCTURE_ELEMENT_COLORS defaults for untouched entries.
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { INFRASTRUCTURE_ELEMENT_COLORS, type InfrastructureElementId } from '../constants/element-colors';

interface InfrastructureElementColorsState {
  /** Only colours the user has explicitly changed */
  overrides: Partial<Record<InfrastructureElementId, string>>;
}

interface InfrastructureElementColorsActions {
  /** Return override if set, otherwise default */
  getColor: (id: InfrastructureElementId) => string;
  /** Set a user override */
  setColor: (id: InfrastructureElementId, hex: string) => void;
  /** Remove a single override (revert to default) */
  resetColor: (id: InfrastructureElementId) => void;
  /** Remove all overrides */
  resetAll: () => void;
}

export type InfrastructureElementColorsStore = InfrastructureElementColorsState & InfrastructureElementColorsActions;

export const useInfrastructureElementColorsStore = create<InfrastructureElementColorsStore>()(
  devtools(
    persist(
      (set, get) => ({
        overrides: {},

        getColor(id: InfrastructureElementId): string {
          return get().overrides[id] ?? INFRASTRUCTURE_ELEMENT_COLORS[id];
        },

        setColor(id: InfrastructureElementId, hex: string): void {
          set(
            (s) => ({ overrides: { ...s.overrides, [id]: hex } }),
            false,
            'setColor',
          );
        },

        resetColor(id: InfrastructureElementId): void {
          set(
            (s) => {
              const next = { ...s.overrides };
              delete next[id];
              return { overrides: next };
            },
            false,
            'resetColor',
          );
        },

        resetAll(): void {
          set({ overrides: {} }, false, 'resetAll');
        },
      }),
      { name: 'infrastructure-element-colors' },
    ),
    { name: 'InfrastructureElementColors' },
  ),
);
